import { MessageSquareText } from "lucide-react"
import { useConversationOpportunitiesQuery } from "modules/conversations/hooks/useConversationOpportunitiesQuery"
import { Loading } from "modules/shared/components/Loading"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { OpportunityContent } from "./OpportunityContent"

type Props = {
  customerId: string
  onConversationClick: (conversationId: string) => void
}

const PAGE_SIZE = 100

export function OpportunitiesTimeline({ customerId, onConversationClick }: Props) {
  const { t } = useTranslation()

  const { data: opportunities, isPending } = useConversationOpportunitiesQuery({
    query: {
      page: 1,
      total: PAGE_SIZE,
      filterBy: { customerId },
      sortBy: { field: "createdAt", order: "asc" },
    },
  })

  const hasData = opportunities && opportunities.length > 0

  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isPending && hasData) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" })
    }
  }, [isPending, hasData])

  if (isPending) return <Loading />

  if (!hasData)
    return (
      <div className="flex flex-col gap-2 px-4 py-8 items-center justify-center text-center max-w-md mx-auto">
        <div className="flex flex-col gap-1">
          <span className="text-md">{t("conversations.opportunities.timeline.noData")}</span>
          <span className="text-sm text-muted-foreground">
            {t("conversations.opportunities.timeline.noDataDescription")}
          </span>
        </div>
      </div>
    )

  // Build a flat list of timeline nodes: conversation-start entries + items
  type ConversationNode = {
    kind: "conversation"
    conversationId: string
    campaignName: string | null | undefined
    createdAt: string
  }

  type ItemNode = {
    kind: "item"
    opportunity: (typeof opportunities)[number]
  }

  type TimelineNode = ConversationNode | ItemNode

  const nodes: TimelineNode[] = []
  let lastConversationId: string | null = null

  for (const opportunity of opportunities) {
    if (opportunity.conversationId !== lastConversationId) {
      nodes.push({
        kind: "conversation",
        conversationId: opportunity.conversationId,
        campaignName: opportunity.campaign?.name,
        createdAt: opportunity.createdAt,
      })
      lastConversationId = opportunity.conversationId
    }
    nodes.push({ kind: "item", opportunity })
  }

  return (
    <div className="group relative py-4">
      {nodes.map((node, index) => {
        const isLast = index === nodes.length - 1

        if (node.kind === "conversation") {
          return (
            <div
              key={`conv-${node.conversationId}`}
              className="relative flex w-full cursor-pointer group/conv"
              onClick={() => onConversationClick(node.conversationId)}
            >
              <div className="absolute flex flex-col items-center left-4 top-0 mt-3.5 h-full z-10">
                <div className="shrink-0 size-2.5 rounded-full bg-muted-foreground/40 z-10 transition-colors duration-150 group-hover/conv:bg-muted-foreground" />
                {!isLast && <div className="w-0.5 bg-border flex-1" />}
              </div>
              <div className="w-full py-2 pr-4 pl-12 flex items-center gap-2 rounded-lg transition-colors duration-150 group-hover/conv:bg-secondary">
                <MessageSquareText className="size-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-medium text-muted-foreground truncate">
                  {node.campaignName ?? t("campaigns.untitled")}
                </span>
              </div>
            </div>
          )
        }

        const { opportunity } = node

        return (
          <div key={opportunity.id} className="group/item relative flex w-full">
            <div className="absolute flex flex-col items-center left-4 top-0 mt-6 h-full z-10">
              <div className="shrink-0 size-2.5 bg-chart-1 opacity-60 rounded-full z-10 transition-all duration-150 group-hover/item:opacity-100" />
              {!isLast && <div className="w-0.5 bg-border flex-1" />}
            </div>
            <div className="transition-colors duration-150 rounded-lg w-full py-4 pr-4 pl-12 h-auto items-start justify-start whitespace-normal">
              <OpportunityContent opportunity={opportunity} key={opportunity.id} />
            </div>
          </div>
        )
      })}
      <div ref={bottomRef} />
    </div>
  )
}
